import React from "react";
import { Link } from "react-router-dom";
import { useShopSections } from "../../lib/useShopSections";
import { EbookCard } from "../../components/ShopCards";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
    Activity, ArrowRight, Award, BadgePercent, Bed, Bone, Brain, CircleDot, Dna, Droplet, Droplets,
    Dumbbell, Eye, Flame, FlaskConical, Gift, Heart, HeartPulse, Leaf, Mars, PersonStanding, Pill,
    Quote, Salad, Scissors, Shield, ShieldCheck, Sparkles, Star, Stethoscope, Sun, Venus, Zap,
} from "lucide-react";
import { T } from "../EditContext";
import { ButtonRing, useBusyAction } from "../../components/LoadingRing";
import SocialFeed from "../../components/SocialFeed";
import Testimonials from "../../components/Testimonials";
import ActivesSection from "../../components/formulation/ActivesSection";
import ComboSection from "../../components/formulation/ComboSection";
import FormulationExplorer from "../../components/formulation/FormulationExplorer";
import ConsultationExperience from "../../pages/Consultation";
import BlogIndex from "../../pages/Blog";
import { useFormulation } from "../../components/formulation/useFormulation";
import { fetchProducts } from "../../lib/api";
import { LIFESTYLE_BEDROOM, formatINR } from "../../lib/constants";
import { useCart } from "../../context/CartContext";

export const ICONS = {
    zap: Zap, heart: Heart, shield: ShieldCheck, "shield-alt": Shield, leaf: Leaf, award: Award,
    sparkles: Sparkles, flask: FlaskConical, activity: Activity, sun: Sun, stethoscope: Stethoscope,
};

export const Heading = ({ f, value, className = "" }) => (
    <T f={f} value={value} as="h2" multiline
        className={`font-display text-4xl md:text-5xl uppercase tracking-tight whitespace-pre-line ${className}`} />
);

// Separator icons for the scrolling band — themed so staff pick by meaning, not by icon name.
export const BAND_ICONS = {
    sparkle: { label: "Sparkle", Icon: Sparkles },
    star: { label: "Star", Icon: Star },
    offer: { label: "Offer / discount", Icon: BadgePercent },
    gift: { label: "Gift", Icon: Gift },
    health: { label: "Health", Icon: HeartPulse },
    heart: { label: "Heart", Icon: Heart },
    herbal: { label: "Herbal / natural", Icon: Leaf },
    tablet: { label: "Tablet", Icon: Pill },
    hair: { label: "Hair", Icon: Scissors },
    men: { label: "Men", Icon: Mars },
    women: { label: "Women", Icon: Venus },
    body: { label: "Human body", Icon: PersonStanding },
    liver: { label: "Liver", Icon: Droplet },
    gut: { label: "Gut", Icon: CircleDot },
    bones: { label: "Bones", Icon: Bone },
    eyes: { label: "Eyes", Icon: Eye },
    brain: { label: "Brain", Icon: Brain },
    energy: { label: "Energy", Icon: Zap },
    strength: { label: "Strength", Icon: Dumbbell },
    immunity: { label: "Immunity", Icon: ShieldCheck },
    science: { label: "Science", Icon: FlaskConical },
    dna: { label: "DNA", Icon: Dna },
    sleep: { label: "Sleep", Icon: Bed },
    doctor: { label: "Doctor", Icon: Stethoscope },
    nutrition: { label: "Nutrition", Icon: Salad },
    hydration: { label: "Hydration", Icon: Droplets },
    vitality: { label: "Vitality", Icon: Activity },
    award: { label: "Award", Icon: Award },
    flame: { label: "Flame", Icon: Flame },
};

export const MarqueeSection = ({ props: p = {} }) => {
    // Phrases used to be plain strings; they can now carry their own icon.
    const phrases = (p.items || []).map((it) => (typeof it === "string" ? { text: it } : it || {}));
    const angle = p.tilt === "angled" ? Number(p.tilt_angle ?? -3) : 0;
    const seconds = Number(p.tilt_seconds) > 0 ? Number(p.tilt_seconds) : 34;
    const iconSize = Number(p.icon_size) > 0 ? Number(p.icon_size) : 22;

    // Thickness / type size: preset gives the baseline, explicit px values override it.
    const PRESETS = { slim: [12, 16], normal: [24, 32], bold: [40, 56] };
    const [presetPadM, presetPad] = PRESETS[p.thickness_preset] || PRESETS.normal;
    const num = (v) => (v === 0 || (v && Number(v) > 0) ? Number(v) : null);
    const pad = num(p.band_pad) ?? presetPad;
    const padM = num(p.band_pad_mobile) ?? num(p.band_pad) ?? presetPadM;
    const height = num(p.band_height);
    const heightM = num(p.band_height_mobile) ?? height;
    const fs = num(p.font_size) ?? 48;
    const fsM = num(p.font_size_mobile) ?? num(p.font_size) ?? 30;
    const bandVars = {
        "--band-pad": `${pad}px`,
        "--band-pad-m": `${padM}px`,
        "--band-fs": `${fs}px`,
        "--band-fs-m": `${fsM}px`,
        ...(height ? { "--band-h": `${height}px` } : {}),
        ...(heightM ? { "--band-h-m": `${heightM}px` } : {}),
        ...(p.font_weight && p.font_weight !== "auto" ? { "--band-fw": p.font_weight } : {}),
    };

    const separator = (key) => {
        const { Icon } = BAND_ICONS[key || p.icon] || {};
        if (Icon) {
            return <Icon className="shrink-0" strokeWidth={2.2}
                style={{ width: iconSize, height: iconSize, color: p.icon_hex || undefined }} />;
        }
        return <span className={p.icon_hex ? "text-xl" : "text-gold text-xl"}
            style={p.icon_hex ? { color: p.icon_hex } : undefined}>✦</span>;
    };

    const band = (
        <section className={`rhm-band border-y border-white/10 overflow-hidden ${p.bg_hex ? "" : "marquee-band"}`}
            data-testid="marquee-band"
            style={{
                ...bandVars,
                ...(p.bg_hex ? { backgroundColor: p.bg_hex } : {}),
                ...(angle ? { transform: `rotate(${angle}deg)`, width: "112%", marginLeft: "-6%" } : {}),
            }}>
            <div className={`marquee-track font-display uppercase ${p.text_hex ? "" : "text-white/25"}`}
                style={{ animationDuration: `${seconds}s`, ...(p.text_hex ? { color: p.text_hex, opacity: 1 } : {}) }}>
                {[...Array(2)].map((_, k) => (
                    <div key={k} className="flex items-center gap-8 pr-8 whitespace-nowrap">
                        {phrases.map((ph, i) => (
                            <React.Fragment key={`${ph.text}-${i}`}>
                                <span>{ph.text}</span>
                                {separator(ph.icon)}
                            </React.Fragment>
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );

    const wrapped = angle
        ? <div className="overflow-hidden py-6 md:py-10" data-testid="marquee-angled">{band}</div>
        : band;
    if (!p.href) return wrapped;
    return /^https?:\/\//i.test(p.href)
        ? <a href={p.href} target="_blank" rel="noreferrer" className="block" data-testid="marquee-link">{wrapped}</a>
        : <Link to={p.href} className="block" data-testid="marquee-link">{wrapped}</Link>;
};

export const BenefitsSection = ({ props: p = {} }) => (
    <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
                <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
                <Heading f="heading" value={p.heading} />
            </div>
            <T f="note" value={p.note} as="p" multiline className="max-w-md text-zinc-400" />
        </div>
        <div className="grid md:grid-cols-3 gap-5">
            {(p.cards || []).filter((c) => c.visible !== false).map((c, idx) => {
                const Icon = ICONS[c.icon] || Sparkles;
                return (
                    <motion.div key={c.title || idx} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }} viewport={{ once: true }}
                        className="relative p-8 rounded-sm bg-bg1 border border-white/10 hover:border-lime/40 transition-colors group">
                        <div className="w-12 h-12 rounded-sm bg-lime/15 text-lime flex items-center justify-center mb-6 group-hover:bg-lime group-hover:text-bg0 transition-colors">
                            <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight">{c.title}</h3>
                        <p className="mt-3 text-zinc-400">{c.desc}</p>
                    </motion.div>
                );
            })}
        </div>
    </section>
);

export const FormulationSection = ({ props: p = {} }) => {
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const product = products.find((x) => x.slug === (p.product_slug || "redrive-max"));
    const form = useFormulation(product);
    if (form.enabled) {
        return (
            <>
                <ActivesSection sections={form.sections} actives={form.actives} comboMap={form.comboMap}
                    trademarks={form.trademarks} flush />
                <FormulationExplorer sections={form.sections} combos={form.combos} byCombo={form.byCombo}
                    comboMap={form.comboMap} basisLabel={form.basisLabel} />
            </>
        );
    }
    return (
        <section className="bg-bg1 border-y border-white/10 py-12 md:py-16">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="eyebrow mb-3 flex items-center gap-2">
                    <FlaskConical className="w-3.5 h-3.5" /> <T f="eyebrow" value={p.eyebrow} />
                </div>
                <Heading f="heading" value={p.heading} className="max-w-2xl" />
                <T f="body" value={p.body} as="p" multiline className="mt-5 text-zinc-400 max-w-xl" />
            </div>
        </section>
    );
};

const LifestyleAddButton = ({ product, label, addItem }) => {
    const [adding, runAdd] = useBusyAction();
    return (
        <button onClick={() => runAdd(() => addItem(product, 1))} disabled={adding}
            data-testid="lifestyle-add-to-cart-button" className="btn-pill btn-primary">
            {adding ? <ButtonRing label="ADDING…" /> : `${label} — ${formatINR(product.price)}`}
        </button>
    );
};


export const LifestyleBandSection = ({ props: p = {} }) => {
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const product = products.find((x) => x.slug === (p.product_slug || "redrive-max"));
    const { addItem } = useCart();
    return (
        <section className="relative h-[480px] md:h-[560px] overflow-hidden" data-testid="lifestyle-band">
            <img src={p.image_url || LIFESTYLE_BEDROOM} alt={p.image_alt || ""} data-testid="lifestyle-band-image"
                className="absolute inset-0 w-full h-full object-cover object-[68%_25%] opacity-75" />
            <div className="absolute inset-0 bg-gradient-to-r from-bg0 via-bg0/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-bg0 via-bg0/40 to-transparent" />
            <div className="relative h-full mx-auto max-w-7xl px-6 md:px-12 flex items-end pb-16">
                <div className="max-w-2xl">
                    <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-4" />
                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight">
                        <T f="heading_prefix" value={p.heading_prefix} />{" "}
                        <T f="heading_accent" value={p.heading_accent} className="text-lime" />
                        <T f="heading_suffix" value={p.heading_suffix} />
                    </h2>
                    {product && p.cta_label && (
                        <div className="mt-7 flex items-center gap-4">
                            <LifestyleAddButton product={product} label={p.cta_label} addItem={addItem} />
                            {product.compare_at_price && (
                                <div className="text-sm text-zinc-400 line-through">{formatINR(product.compare_at_price)}</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const ACCENTS = { lime: "var(--lime)", gold: "var(--gold)" };

export const PatentedSection = ({ props: p = {} }) => (
    <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        <div className="eyebrow mb-3 flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> <T f="eyebrow" value={p.eyebrow} />
        </div>
        <Heading f="heading" value={p.heading} className="mb-12 max-w-3xl" />
        <div className="grid md:grid-cols-2 gap-5">
            {(p.cards || []).filter((c) => c.visible !== false).map((c) => {
                const color = ACCENTS[c.color] || ACCENTS.gold;
                return (
                    <Link key={c.tag} to={c.link || "/blog"}
                        data-testid={`patented-card-${(c.tag || "").toLowerCase().replace(/\W/g, "")}`}
                        className="group relative p-8 rounded-sm bg-bg1 border border-white/10 hover:border-white/30 transition-colors overflow-hidden">
                        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full pointer-events-none"
                            style={{ background: `radial-gradient(circle, color-mix(in srgb, ${color} 14%, transparent) 0%, transparent 70%)` }} />
                        <div className="relative">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-display uppercase tracking-tight text-xs font-bold"
                                style={{
                                    background: `color-mix(in srgb, ${color} 15%, transparent)`, color,
                                    border: `1px solid color-mix(in srgb, ${color} 40%, transparent)`,
                                }}>
                                <Award className="w-3.5 h-3.5" /> {c.tag}
                            </div>
                            <h3 className="mt-5 font-display text-2xl md:text-3xl uppercase tracking-tight">{c.title}</h3>
                            <p className="mt-3 text-zinc-400 leading-relaxed">{c.desc}</p>
                            <div className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-widest font-bold text-white group-hover:text-lime transition-colors">
                                {c.link_label || "Read more"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </Link>
                );
            })}
        </div>
    </section>
);

export const ConsultationCtaSection = ({ props: p = {} }) => (
    <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        <div className="border border-gold/30 bg-gold/[0.06] p-8 md:p-12 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-2xl">
                <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
                <Heading f="heading" value={p.heading} />
                <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-300 leading-relaxed" />
            </div>
            <Link to={p.cta_href || "/consultation"} className="btn-pill btn-primary shrink-0" data-testid="home-consultation-cta">
                <Stethoscope className="w-4 h-4 mr-2" /> <T f="cta_label" value={p.cta_label} />
            </Link>
        </div>
    </section>
);

export const TestimonialsSection = ({ props: p = {} }) => (
    <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16 border-t border-white/5">
        <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
        <Heading f="heading" value={p.heading} className="mb-12 max-w-2xl" />
        <Testimonials slug={p.product_slug || "redrive-max"} limit={Number(p.limit) || 6} />
    </section>
);

export const SocialWallSection = ({ props: p = {} }) => (
    <section className="bg-bg1 border-y border-white/10 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
                <div>
                    <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />
                    <Heading f="heading" value={p.heading} />
                </div>
                <T f="note" value={p.note} as="p" multiline className="text-zinc-400 max-w-md" />
            </div>
            <SocialFeed limit={Number(p.limit) || 6} columns={Number(p.columns) || 3} slug={p.product_slug || "redrive-max"} />
        </div>
    </section>
);

export const RichTextSection = ({ props: p = {} }) => (
    <section className={`mx-auto max-w-4xl px-6 md:px-12 py-10 md:py-14 ${p.align === "center" ? "text-center" : ""}`}>
        {p.eyebrow && <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />}
        <Heading f="heading" value={p.heading} />
        <T f="body" value={p.body} as="div" multiline
            className="mt-6 text-zinc-300 leading-relaxed whitespace-pre-line text-base md:text-lg" />
        {p.cta_label && (
            <Link to={p.cta_href || "/"} className="btn-pill btn-primary mt-8 inline-flex" data-testid="richtext-cta">
                <T f="cta_label" value={p.cta_label} />
            </Link>
        )}
    </section>
);

export const FaqSection = ({ props: p = {} }) => (
    <section className="mx-auto max-w-4xl px-6 md:px-12 py-10 md:py-14">
        {p.eyebrow && <T f="eyebrow" value={p.eyebrow} as="div" className="eyebrow mb-3" />}
        <Heading f="heading" value={p.heading} />
        <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {(p.items || []).filter((i) => i.visible !== false).map((item, idx) => (
                <details key={idx} className="group py-5" data-testid={`faq-item-${idx}`}>
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                        <span className="font-display text-lg md:text-xl uppercase tracking-tight">{item.question}</span>
                        <span className="text-gold mono text-xs mt-1 group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{item.answer}</p>
                </details>
            ))}
        </div>
    </section>
);

// Homepage journal strip — always pulls the latest published articles from the Blog CMS.
export const BlogHighlightSection = ({ props: p = {} }) => {
    const h = p.highlight || {};
    const slug = h.post_slug || "";
    const { data: post } = useQuery({
        queryKey: ["blog-highlight", slug],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${slug}`)).json(),
        enabled: !!slug,
        staleTime: 60000,
    });
    if (!slug || !post?.slug) return null;

    const text = h.text || post.excerpt || "";
    const image = h.show_image !== false ? post.featured_image?.url : null;
    const quoteOnly = p.layout === "quote" || !image;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16" data-testid="home-blog-highlight">
            <T f="eyebrow" value={p.eyebrow || "From the blog"} as="div" className="eyebrow mb-3" />
            {p.heading && <Heading f="heading" value={p.heading} className="mb-10" />}

            <Link to={`/blog/${post.slug}`} data-testid={`blog-highlight-${post.slug}`}
                className={`group grid gap-8 md:gap-12 items-center border border-white/10 bg-bg1 p-7 md:p-12 hover:border-gold/50 transition-colors ${
                    quoteOnly ? "" : "md:grid-cols-[minmax(0,1fr)_420px]"}`}>
                <div className="min-w-0">
                    {h.show_title !== false && (
                        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-[0.95]"
                            data-testid="blog-highlight-title">
                            {post.title}
                        </h3>
                    )}
                    <blockquote className={`relative border-l-2 border-gold pl-6 text-zinc-300 leading-relaxed ${
                        h.show_title === false ? "" : "mt-7"} ${quoteOnly ? "text-xl md:text-2xl" : "text-lg"}`}
                        data-testid="blog-highlight-text">
                        <Quote className="w-5 h-5 text-gold/60 mb-3" />
                        {text}
                    </blockquote>
                    <span className="mt-8 inline-flex items-center gap-2 mono text-[10px] tracking-[0.18em] text-gold">
                        {(p.cta_label || "Read the full article").toUpperCase()}
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                </div>
                {!quoteOnly && (
                    <div className="overflow-hidden border border-white/10">
                        <img src={image} alt={post.featured_image?.alt || post.title} loading="lazy"
                            data-testid="blog-highlight-image"
                            className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                )}
            </Link>
        </section>
    );
};

export const ImageBannerSection = ({ props: p = {} }) => {
    const height = { short: "h-[300px] md:h-[380px]", medium: "h-[420px] md:h-[520px]", tall: "h-[520px] md:h-[640px]" }[p.height || "medium"];
    return (
        <section className={`relative overflow-hidden rounded-sm mx-4 md:mx-12 mt-12 md:mt-16 ${height}`}
            data-testid="section-image-banner">
            {p.image_url && (
                <img src={p.image_url} alt={p.image_alt || ""} className="absolute inset-0 w-full h-full object-cover opacity-60" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-bg0 via-bg0/70 to-transparent" />
            <div className="relative h-full flex items-end p-8 md:p-14">
                <div className="max-w-3xl">
                    <T f="eyebrow" value={p.eyebrow} as="div" className="text-lime uppercase tracking-[0.3em] text-xs mb-4" />
                    <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">
                        <T f="heading" value={p.heading} />{" "}
                        <T f="heading_accent" value={p.heading_accent} className="text-lime" />
                    </h1>
                    <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-300 max-w-xl" />
                </div>
            </div>
        </section>
    );
};

export const ScienceFormulationSection = ({ props: p = {} }) => {
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const product = products.find((x) => x.slug === (p.product_slug || "redrive-max"));
    const form = useFormulation(product);
    if (!form.enabled) {
        return <div className="mx-auto max-w-7xl px-6 md:px-12 py-20 text-zinc-500">Loading formulation…</div>;
    }
    return (
        <div data-testid="section-science-formulation">
            <ComboSection sections={form.sections} combos={form.combos} byCombo={form.byCombo} basisLabel={form.basisLabel} />
            <ActivesSection sections={form.sections} actives={form.actives} comboMap={form.comboMap} trademarks={form.trademarks} />
            <FormulationExplorer sections={form.sections} combos={form.combos} byCombo={form.byCombo}
                comboMap={form.comboMap} basisLabel={form.basisLabel} />
        </div>
    );
};

export const ConsultExperienceSection = () => (
    <div data-testid="section-consult-experience"><ConsultationExperience /></div>
);

export const BlogIndexSection = ({ props: p = {} }) => (
    <div data-testid="section-blog-index"><BlogIndex heading={p.heading} sub={p.sub} /></div>
);

export const JournalSection = ({ props: p = {} }) => {
    const limit = Number(p.limit) || 3;
    const source = p.source || "settings";
    const { data } = useQuery({
        queryKey: ["journal-section", source, p.category_slug || "", limit],
        queryFn: async () => {
            const base = process.env.REACT_APP_BACKEND_URL;
            if (source === "settings") return (await (await fetch(`${base}/api/blog/home?limit=${limit}`)).json());
            const params = new URLSearchParams({ limit: String(limit) });
            if (source === "featured") params.set("featured", "true");
            if (source === "category" && p.category_slug) params.set("category", p.category_slug);
            const posts = await (await fetch(`${base}/api/blog/posts?${params}`)).json();
            return { heading: p.heading, posts };
        },
        staleTime: 60000,
    });
    const posts = data?.posts || [];
    if (!posts.length) return null;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16" data-testid="home-journal">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                    <T f="eyebrow" value={p.eyebrow || "Journal"} as="div" className="eyebrow mb-3" />
                    <Heading f="heading" value={p.heading || data?.heading || "From the ReHealthMe Journal"} />
                </div>
                <Link to="/blog" className="mono text-[10px] tracking-[0.18em] text-gold inline-flex items-center gap-2"
                    data-testid="journal-all-link">
                    {(p.cta_label || "All articles").toUpperCase()} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
                {posts.map((post) => (
                    <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`journal-card-${post.slug}`}
                        className="group border border-white/10 bg-bg1 hover:border-gold/40 transition-all hover:-translate-y-1">
                        <div className="h-44 bg-bg0 overflow-hidden">
                            {post.featured_image?.url
                                ? <img src={post.featured_image.url} alt={post.featured_image.alt || post.title}
                                    loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                : <div className="w-full h-full bg-gradient-to-br from-lime/25 to-bg2" />}
                        </div>
                        <div className="p-5">
                            {post.category && (
                                <div className="mono text-[9px] tracking-[0.18em] text-gold">{post.category.name.toUpperCase()}</div>
                            )}
                            <h3 className="font-display text-xl uppercase tracking-tight mt-2">{post.title}</h3>
                            <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{post.excerpt}</p>
                            <div className="mt-4 mono text-[9px] tracking-[0.16em] text-zinc-500">
                                {post.reading_minutes} MIN READ
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
};

// Homepage ebooks strip — pulls digital guides straight from the product catalogue.
export const EbooksSection = ({ props: p = {} }) => {
    const limit = Number(p.limit) || 4;
    const sections = useShopSections();
    const { data: all = [] } = useQuery({
        queryKey: ["products"],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`)).json(),
        staleTime: 60000,
    });
    const ebooks = all.filter((x) => (x.shop_type || "product") === "ebook").slice(0, limit);
    if (!sections.ebooks || !ebooks.length) return null;

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16" data-testid="home-ebooks">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                    <T f="eyebrow" value={p.eyebrow || "Digital library"} as="div" className="eyebrow mb-3" />
                    <Heading f="heading" value={p.heading || "Ebooks & Guides"} />
                    <T f="sub" value={p.sub || "Practical, readable guides from the ReHealthMe team."} as="p"
                        className="mt-3 text-sm text-zinc-500 max-w-xl" />
                </div>
                <Link to="/shop" className="mono text-[10px] tracking-[0.18em] text-gold inline-flex items-center gap-2"
                    data-testid="ebooks-all-link">
                    {(p.cta_label || "Browse the shop").toUpperCase()} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {ebooks.map((b) => <EbookCard key={b.id} p={b} />)}
            </div>
        </section>
    );
};
